import posthog from 'posthog-js'
import Cookies from 'universal-cookie'
import '@tailwindplus/elements'

const trackingCookieName = 'goodcrowd-tracking-accepted'
const cookies = new Cookies()

const isMobile = () => window.innerWidth < 768

const sendBrowserEvent = (eventName: string) => {
  const deviceType = isMobile() ? 'mobile' : 'desktop'
  // Send event via fetch to avoid GraphQL dependency
  fetch('/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `
        mutation BrowserEvent($eventName: String, $deviceType: String) {
          browserEvent(input: { eventName: $eventName, deviceType: $deviceType }) {
            success
          }
        }
      `,
      variables: { eventName, deviceType },
    }),
  }).catch(() => {
    // Silently fail if GraphQL endpoint is unavailable
  })
}

function initCookieBanner() {
  const container = document.getElementById('cookie-banner-container')
  if (!container) return

  // Avoid duplicate initialization
  if (container.dataset.initialized === 'true') return
  container.dataset.initialized = 'true'

  const banner = document.getElementById('cookie-banner')
  const acceptButton = document.getElementById('cookie-banner-accept')
  const rejectButton = document.getElementById('cookie-banner-reject')
  const moreInfoButton = document.getElementById('cookie-banner-more-info')
  const cookieBannerLink = document.getElementById('cookie-banner-link')
  const modalElement = document.getElementById('cookie-modal')
  const dialog = modalElement?.querySelector('dialog') as HTMLDialogElement | null
  const modalCloseButton = document.getElementById('cookie-modal-close')

  const trackingChoice = container.dataset.trackingChoice
  const userAgentIsBot = /bot|crawler|spider|crawling/i.test(navigator.userAgent)

  // Don't show banner if user already made a choice or is a bot
  if (trackingChoice === 'accepted' || trackingChoice === 'rejected' || userAgentIsBot) {
    return
  }

  // Show the banner
  if (banner) {
    banner.classList.remove('hidden')
    sendBrowserEvent('tracking-banner-show')
  }

  // Auto-reject tracking after interaction
  let pageUsed = false
  let autoRejectTimer: number | null = null
  const firstScrollY = window.scrollY

  const startAutoRejectTimer = () => {
    if (autoRejectTimer || !pageUsed) return
    autoRejectTimer = window.setTimeout(() => {
      rejectTracking(true)
    }, 10000)
  }

  const handleScroll = () => {
    if (Math.abs(window.scrollY - firstScrollY) > 10) {
      pageUsed = true
      startAutoRejectTimer()
      window.removeEventListener('scroll', handleScroll)
    }
  }

  const handleClick = (event: MouseEvent) => {
    if (banner && !banner.contains(event.target as Node)) {
      pageUsed = true
      startAutoRejectTimer()
      document.removeEventListener('click', handleClick)
    }
  }

  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', handleClick)

  const acceptTracking = () => {
    cookies.set(trackingCookieName, 'accepted', { path: '/', expires: new Date(2099, 1, 1) })

    if (!posthog.has_opted_in_capturing()) {
      setTimeout(() => {
        console.log('opt in tracking')
        posthog.opt_in_capturing()
      })
    }

    sendBrowserEvent('tracking-banner-accepted')

    if (banner) banner.classList.add('hidden')
    if (autoRejectTimer) clearTimeout(autoRejectTimer)
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('click', handleClick)
  }

  const rejectTracking = (isTimeout = false) => {
    cookies.set(trackingCookieName, 'rejected', { path: '/', expires: new Date(2099, 1, 1) })

    if (!posthog.has_opted_out_capturing()) {
      posthog.opt_out_capturing()
    }

    sendBrowserEvent(isTimeout ? 'tracking-banner-rejected-by-timeout' : 'tracking-banner-rejected')

    if (banner) banner.classList.add('hidden')
    if (autoRejectTimer) clearTimeout(autoRejectTimer)
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('click', handleClick)
  }

  const openModal = (event?: Event) => {
    if (event) event.preventDefault()
    if (dialog && !dialog.open) {
      dialog.showModal()
    }
    // Stop auto-reject when user opens modal
    if (autoRejectTimer) {
      clearTimeout(autoRejectTimer)
      autoRejectTimer = null
    }
  }

  const closeModal = (event?: Event) => {
    if (event) event.preventDefault()
    if (dialog && dialog.open) {
      dialog.close()
    }
  }

  // Close modal when clicking outside (on backdrop)
  if (dialog) {
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect()
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width

      if (!isInDialog) {
        dialog.close()
      }
    })
  }

  // Button event listeners
  if (acceptButton) {
    acceptButton.addEventListener('click', acceptTracking)
  }

  if (rejectButton) {
    rejectButton.addEventListener('click', () => rejectTracking(false))
  }

  if (moreInfoButton) {
    moreInfoButton.addEventListener('click', openModal)
  }

  if (modalCloseButton) {
    modalCloseButton.addEventListener('click', closeModal)
  }

  // Cookie banner link in footer
  if (cookieBannerLink) {
    console.log('cookie banner link')
    cookieBannerLink.addEventListener('click', (event) => {
      console.log('cookie banner link click')
      event.preventDefault()
      cookies.set(trackingCookieName, 'edit', { path: '/', expires: new Date(2099, 1, 1) })
      // Force banner to show by updating the data attribute
      if (container) {
        container.dataset.trackingChoice = 'edit'
      }
      if (banner) {
        banner.classList.remove('hidden')
      }
    })
  }
}

document.addEventListener('htmx:afterSettle', initCookieBanner)
document.addEventListener('DOMContentLoaded', initCookieBanner)
window.addEventListener('htmx:historyRestore', initCookieBanner)

// Fallback event delegation for cookie banner link (in case HTMX interferes)
document.addEventListener('click', (event) => {
  const target = event.target as HTMLElement
  if (target.id === 'cookie-banner-link' || target.closest('#cookie-banner-link')) {
    event.preventDefault()
    console.log('Cookie banner link clicked via delegation')
    cookies.set(trackingCookieName, 'edit', { path: '/', expires: new Date(2099, 1, 1) })

    const container = document.getElementById('cookie-banner-container')
    const banner = document.getElementById('cookie-banner')

    if (container) {
      container.dataset.trackingChoice = 'edit'
      container.dataset.initialized = 'false' // Allow re-initialization
    }
    if (banner) {
      banner.classList.remove('hidden')
    }

    // Re-initialize to set up event listeners
    initCookieBanner()
  }
})
